/**
 * ConfirmDialog — promise-based in-app confirm dialog (replaces window.confirm).
 *
 * Usage:
 *   import { confirm, ConfirmContainer } from '../components/ConfirmDialog.jsx'
 *
 *   // Anywhere:
 *   const ok = await confirm({
 *     title: 'Discard changes?',
 *     message: 'You have unsaved changes. Leave this page?',
 *     confirmLabel: 'Discard',
 *     cancelLabel:  'Keep editing',
 *     destructive:  true,
 *   })
 *   if (!ok) return
 *
 *   // In App.jsx (once, at the root):
 *   <ConfirmContainer />
 *
 * Implementation mirrors Toast.jsx — a module-level store + a single container
 * that subscribes via a listener. No React context required.
 */

import { useState, useEffect } from 'react'
import { useTheme } from '../context/ThemeProvider.jsx'

const C = {
  obsidian: '#0E0C12',
  cellar:   '#3A0D18',
  burgundy: '#7B2040',
  gold:     '#C4965A',
  ivory:    '#F4EFE7',
}

// ── Store ─────────────────────────────────────────────────────────────────────
let _pending  = null   // { id, opts, resolve }
let _listeners = []

function notify() {
  _listeners.forEach(fn => fn(_pending))
}

/**
 * Show a confirm dialog. Returns a Promise that resolves to true (confirm)
 * or false (cancel / backdrop click / Escape).
 *
 * opts: { title, message, confirmLabel?, cancelLabel?, destructive? }
 */
export function confirm(opts) {
  // If one is already open, auto-reject the old one (shouldn't happen in practice)
  if (_pending) {
    try { _pending.resolve(false) } catch {}
  }
  return new Promise((resolve) => {
    _pending = {
      id: Date.now(),
      opts: {
        title:        opts?.title        || 'Are you sure?',
        message:      opts?.message      || '',
        confirmLabel: opts?.confirmLabel || 'Confirm',
        cancelLabel:  opts?.cancelLabel  || 'Cancel',
        destructive:  !!opts?.destructive,
      },
      resolve,
    }
    notify()
  })
}

function settle(value) {
  if (!_pending) return
  const { resolve } = _pending
  _pending = null
  notify()
  try { resolve(value) } catch {}
}

// ── Container ─────────────────────────────────────────────────────────────────
export function ConfirmContainer() {
  const { theme } = useTheme()
  const [state, setState] = useState(_pending)

  useEffect(() => {
    const handler = (next) => setState(next)
    _listeners.push(handler)
    return () => { _listeners = _listeners.filter(l => l !== handler) }
  }, [])

  useEffect(() => {
    if (!state) return
    const onKey = (e) => {
      if (e.key === 'Escape') settle(false)
      else if (e.key === 'Enter') settle(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [state])

  if (!state) return null
  const { opts } = state

  return (
    <div
      onClick={() => settle(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'var(--app-modal-overlay)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        style={{
          // Theme-aware modal sheet. Red mode keeps the dark wine
          // gradient; white mode uses a parchment surface so the modal
          // matches the rest of the white-mode chrome instead of
          // appearing as a dark intrusion.
          background: theme.isDark
            ? `linear-gradient(160deg, ${theme.colors.cellar} 0%, ${theme.colors.obsidian} 100%)`
            : theme.colors.surface,
          border: `1px solid ${theme.borders.emphasis}`,
          borderRadius: 18,
          padding: '24px 22px 20px',
          maxWidth: 380,
          width: '100%',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          color: theme.text.primary,
        }}
      >
        <h3
          id="confirm-title"
          style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 22,
            color: theme.text.primary,
            margin: '0 0 8px',
            letterSpacing: '0.02em',
          }}
        >
          {opts.title}
        </h3>
        {opts.message && (
          <p style={{
            fontFamily: 'Raleway, sans-serif',
            fontSize: 14,
            lineHeight: 1.55,
            color: theme.text.secondary,
            margin: '0 0 20px',
          }}>
            {opts.message}
          </p>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={() => settle(false)}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: 12,
              border: `1px solid ${theme.borders.default}`,
              background: 'transparent',
              color: theme.text.primary,
              fontFamily: 'Raleway, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.06em',
              cursor: 'pointer',
            }}
          >
            {opts.cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => settle(true)}
            autoFocus
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: 12,
              border: opts.destructive
                ? '1px solid rgba(248,113,113,0.5)'
                : `1px solid ${C.gold}`,
              background: opts.destructive
                ? 'linear-gradient(135deg, #7B2040, #3A0D18)'
                : `linear-gradient(135deg, ${C.burgundy}, ${C.cellar})`,
              color: C.ivory,
              fontFamily: 'Raleway, sans-serif',
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.06em',
              cursor: 'pointer',
            }}
          >
            {opts.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
